import type {
  AdminAuction,
  AuctionIncrementTierInput,
  PatchAdminV1StoresStoreIdAuctionsIdBody,
  PostAdminV1StoresStoreIdAuctionsBody,
} from '@nucleus-commerce/api-client/admin';
import { fromZonedInput, invariant, toZonedInput } from '@nucleus-commerce/core';

import {
  bpsToPercent,
  percentToBps,
  type AuctionFormInput,
  type AuctionFormOutput,
} from '../schemas/auction-form-schema';
import { DEFAULT_ANTI_SNIPE_MINUTES, DEFAULT_INCREMENTS } from '../schemas/auction-rules';

export type SaveAs = 'draft' | 'scheduled';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** The next whole hour from `now`, then a week of bidding. */
export function emptyAuctionForm(
  currency: string,
  timeZone: string,
  lot: { storeProductId: string; variantId?: string } | null = null,
  now = Date.now(),
): AuctionFormInput {
  const start = Math.ceil((now + 1) / HOUR) * HOUR;
  return {
    storeProductId: lot?.storeProductId ?? '',
    variantId: lot?.variantId ?? '',
    startsAt: toZonedInput(new Date(start).toISOString(), timeZone),
    endsAt: toZonedInput(new Date(start + 7 * DAY).toISOString(), timeZone),
    startingPrice: null,
    reservePrice: null,
    buyNowPrice: null,
    incrementTable: DEFAULT_INCREMENTS.map((tier) => ({
      from: { amount: tier.fromCents, currency },
      step: { amount: tier.incrementCents, currency },
    })),
    antiSnipeMinutes: String(DEFAULT_ANTI_SNIPE_MINUTES),
    buyerPremiumPercent: '',
  };
}

export function auctionToForm(auction: AdminAuction, timeZone: string): AuctionFormInput {
  return {
    storeProductId: auction.lot.storeProductId,
    variantId: auction.lot.variantId,
    startsAt: toZonedInput(auction.startsAt, timeZone),
    endsAt: toZonedInput(auction.originalEndsAt, timeZone),
    startingPrice: auction.startingPrice,
    reservePrice: auction.reservePrice,
    buyNowPrice: auction.buyNowPrice,
    incrementTable: auction.incrementTable.map((tier) => ({
      from: { amount: tier.fromCents, currency: auction.currency },
      step: { amount: tier.incrementCents, currency: auction.currency },
    })),
    antiSnipeMinutes: String(auction.antiSnipeMinutes),
    buyerPremiumPercent: bpsToPercent(auction.buyerPremiumBps),
  };
}

function incrementTable(values: AuctionFormOutput): AuctionIncrementTierInput[] {
  return values.incrementTable.map((row) => {
    invariant(row.from && row.step, 'the form schema requires every tier');
    return { fromCents: row.from.amount, incrementCents: row.step.amount };
  });
}

function instant(value: string, timeZone: string): string {
  const iso = fromZonedInput(value, timeZone);
  invariant(iso, 'the form schema requires a date and time');
  return iso;
}

function terms(values: AuctionFormOutput, timeZone: string) {
  invariant(values.startingPrice, 'the form schema requires a starting price');
  return {
    startsAt: instant(values.startsAt, timeZone),
    endsAt: instant(values.endsAt, timeZone),
    startingPrice: values.startingPrice,
    reservePrice: values.reservePrice,
    buyNowPrice: values.buyNowPrice,
    incrementTable: incrementTable(values),
    antiSnipeMinutes: Number(values.antiSnipeMinutes),
    buyerPremiumBps:
      values.buyerPremiumPercent === '' ? null : percentToBps(values.buyerPremiumPercent),
  };
}

export function toCreateAuction(
  values: AuctionFormOutput,
  saveAs: SaveAs,
  timeZone: string,
  saleId?: string,
): PostAdminV1StoresStoreIdAuctionsBody {
  return {
    storeProductId: values.storeProductId,
    variantId: values.variantId,
    ...terms(values, timeZone),
    ...(saleId ? { saleId } : {}),
    status: saveAs,
  };
}

/** The lot is fixed once created; everything else is sent whole, the reserve and buy now as null to clear them. */
export function toUpdateAuction(
  values: AuctionFormOutput,
  saveAs: SaveAs,
  timeZone: string,
): PatchAdminV1StoresStoreIdAuctionsIdBody {
  return { ...terms(values, timeZone), status: saveAs };
}

/** Backend field paths, prefix stripped, onto the form's fields. */
export function mapAuctionField(field: string): string {
  if (field === 'variantId' || field === 'storeProductId') return field;
  const tier = /^incrementTable\.(\d+)\.(fromCents|incrementCents)$/.exec(field);
  if (tier) return `incrementTable.${tier[1]}.${tier[2] === 'fromCents' ? 'from' : 'step'}`;
  if (field.startsWith('incrementTable')) return 'incrementTable';
  if (field === 'buyerPremiumBps') return 'buyerPremiumPercent';
  return field.replace(/\.(currency|amount)$/, '');
}
